import { useSyncExternalStore } from "react";
import { CODESMITH_EXTENSION_ORIGIN } from "@/lib/codesmithExtensionCapture";

export const CODESMITH_EXTENSION_OPEN_SETTINGS =
  "codesmith-extension-open-settings";
const CODESMITH_EXTENSION_UI_EXPERIMENTS = "codesmith-extension-ui-experiments";
const CODESMITH_EXTENSION_SET_UI_EXPERIMENT =
  "codesmith-extension-set-ui-experiment";

export interface CodesmithExtensionUiExperiment {
  key: string;
  label: string;
  value: "on" | "off";
  defaultValue: "on" | "off";
}

export function postCodesmithExtensionUiExperiments(
  experiments: CodesmithExtensionUiExperiment[],
): void {
  window.parent.postMessage(
    { type: CODESMITH_EXTENSION_UI_EXPERIMENTS, experiments },
    CODESMITH_EXTENSION_ORIGIN,
  );
}

export function parseCodesmithExtensionSetUiExperiment(
  event: MessageEvent<unknown>,
): { key: string; value: "on" | "off" } | null {
  if (
    event.source !== window.parent ||
    event.origin !== CODESMITH_EXTENSION_ORIGIN ||
    !event.data ||
    typeof event.data !== "object"
  ) {
    return null;
  }

  const data = event.data as Record<string, unknown>;
  if (
    data.type !== CODESMITH_EXTENSION_SET_UI_EXPERIMENT ||
    typeof data.key !== "string" ||
    (data.value !== "on" && data.value !== "off")
  ) {
    return null;
  }
  return { key: data.key, value: data.value };
}

export type CodesmithExtensionSettingsGroup = "rollouts";

// The extension's dashboardBridge content script stamps its version on <html>
// once it is running on this page.
const INSTALLED_ATTRIBUTE = "data-blacksmith-extension";

function subscribe(onChange: () => void): () => void {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: [INSTALLED_ATTRIBUTE],
  });
  return () => observer.disconnect();
}

function getSnapshot(): boolean {
  return document.documentElement.hasAttribute(INSTALLED_ATTRIBUTE);
}

function useCodesmithExtensionInstalled(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}

// Panels predating the settings hand-off ignore the request outright, so the
// framed dashboard asks once which settings groups its panel can open.
const CAPABILITIES_REQUEST = "codesmith-extension-capabilities-request";
const CAPABILITIES = "codesmith-extension-capabilities";

let panelSettingsGroups: ReadonlySet<string> = new Set();
const panelListeners = new Set<() => void>();

function onPanelMessage(event: MessageEvent<unknown>): void {
  if (
    event.source !== window.parent ||
    event.origin !== CODESMITH_EXTENSION_ORIGIN ||
    typeof event.data !== "object" ||
    event.data === null
  ) {
    return;
  }
  const { type, settings } = event.data as {
    type?: unknown;
    settings?: unknown;
  };
  if (type !== CAPABILITIES || !Array.isArray(settings)) return;
  panelSettingsGroups = new Set(
    settings.filter((group): group is string => typeof group === "string"),
  );
  for (const listener of panelListeners) listener();
}

function subscribeToPanel(onChange: () => void): () => void {
  panelListeners.add(onChange);
  if (panelListeners.size === 1) {
    window.addEventListener("message", onPanelMessage);
    window.parent.postMessage(
      { type: CAPABILITIES_REQUEST },
      CODESMITH_EXTENSION_ORIGIN,
    );
  }
  return () => {
    panelListeners.delete(onChange);
    if (panelListeners.size === 0) {
      window.removeEventListener("message", onPanelMessage);
    }
  };
}

const subscribeToNothing = () => () => {};

export function useCodesmithExtensionSettingsAvailable(
  group: CodesmithExtensionSettingsGroup,
  { embedded }: { embedded: boolean },
): boolean {
  const installed = useCodesmithExtensionInstalled();
  const panelGroups = useSyncExternalStore(
    embedded ? subscribeToPanel : subscribeToNothing,
    () => panelSettingsGroups,
    () => panelSettingsGroups,
  );
  return embedded ? panelGroups.has(group) : installed;
}

/**
 * Embedded in the panel, the request goes to the panel document; on a
 * top-level dashboard page the content script relays it to the worker, which
 * opens this tab's panel.
 */
export function openCodesmithExtensionSettings(
  group: CodesmithExtensionSettingsGroup,
  { embedded }: { embedded: boolean },
): void {
  const message = { type: CODESMITH_EXTENSION_OPEN_SETTINGS, group };
  if (embedded) {
    window.parent.postMessage(message, CODESMITH_EXTENSION_ORIGIN);
  } else {
    window.postMessage(message, window.location.origin);
  }
}
